import clsx from 'clsx';
import { AriaAttributes, LegacyRef, Ref } from 'react';
import { Wrapper } from './Styled';
import BaseInput, { Props } from 'react-phone-number-input';

// Mirrors the border, hover, focus, and error states of the component
// library's `TextField` so the phone input matches the other form fields.
const wrapperClassName =
  'rounded-md border transition-colors duration-200 ' +
  'focus-within:border-ds-border-focus focus-within:ring-1 focus-within:ring-ds-border-focus';

const validClassName =
  'border-ds-border-control hover:border-ds-border-control-hover';

const invalidClassName = 'border-ds-border-control-negative';

function isAriaInvalid(value: unknown) {
  return value === true || value === 'true';
}

interface PhoneInputProps extends Props<{}> {
  /** Ref to the underlying phone number `<input>` element. */
  inputRef?: Ref<HTMLInputElement>;

  /** Forwarded to the nested `<input>`; also drives the wrapper's error border. */
  'aria-invalid'?: AriaAttributes['aria-invalid'];
}

function PhoneInput({ inputRef, ...props }: PhoneInputProps) {
  // `aria-invalid` reaches the nested `<input>` either through
  // `numberInputProps` or as a top-level prop (e.g. from Semantic's
  // `FormField`), so check both to mirror it on the wrapper border.
  const isInvalid =
    isAriaInvalid(props['aria-invalid']) ||
    isAriaInvalid(
      (props.numberInputProps as AriaAttributes | undefined)?.['aria-invalid']
    );

  return (
    <Wrapper
      className={clsx(
        wrapperClassName,
        isInvalid ? invalidClassName : validClassName
      )}
    >
      {/* react-phone-number-input's types describe this ref as pointing to
          its internal class component, but it actually forwards straight
          through to the underlying `<input>`. */}
      <BaseInput ref={inputRef as LegacyRef<never>} {...props} />
    </Wrapper>
  );
}

export default PhoneInput;
export type { PhoneInputProps };
